// ============================================
// SELECTION CARD COMPONENT
// ============================================

import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import styles from './SelectionCard.module.css';

export interface SelectionCardProps {
  // New simplified API
  selected?: boolean;
  onClick?: () => void;
  title?: string;
  
  // Legacy API
  value?: string;
  label?: string;
  isSelected?: boolean;
  onChange?: (value: string) => void;
  
  // Shared
  description?: string;
  icon?: string | ReactNode;
  type?: 'radio' | 'checkbox';
  disabled?: boolean;
  size?: 'default' | 'compact';
}

export function SelectionCard({
  // New API
  selected,
  onClick,
  title,
  size = 'default',
  
  // Legacy API
  value,
  label,
  isSelected,
  onChange,
  
  // Shared
  description,
  icon,
  type = 'radio',
  disabled = false,
}: SelectionCardProps) {
  // Determine which API is being used
  const isChecked = selected ?? isSelected ?? false;
  const displayLabel = title ?? label ?? '';
  
  const handleClick = () => {
    if (disabled) return;
    
    if (onClick) {
      onClick();
    } else if (onChange && value) {
      onChange(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      role={type}
      aria-checked={isChecked}
      tabIndex={disabled ? -1 : 0}
      className={`${styles.card} ${isChecked ? styles.selected : ''} ${
        disabled ? styles.disabled : ''
      } ${size === 'compact' ? styles.compact : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {/* Selection Indicator */}
      <div className={`${styles.indicator} ${isChecked ? styles.indicatorSelected : ''}`}>
        {isChecked && <Check size={12} strokeWidth={3} />}
      </div>

      {/* Content */}
      <div className={styles.content}>
        {icon && (
          <span className={styles.icon}>
            {typeof icon === 'string' ? icon : icon}
          </span>
        )}
        <div className={styles.text}>
          <span className={styles.label}>{displayLabel}</span>
          {description && (
            <span className={styles.description}>{description}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// SELECTION CARD GROUP
// ============================================

interface SelectionCardGroupProps {
  label?: string;
  error?: string;
  children: React.ReactNode;
  columns?: 1 | 2 | 3;
}

export function SelectionCardGroup({
  label,
  error,
  children,
  columns = 2,
}: SelectionCardGroupProps) {
  return (
    <div className={styles.group}>
      {label && <span className={styles.groupLabel}>{label}</span>}
      <div className={`${styles.grid} ${styles[`cols-${columns}`]}`}>
        {children}
      </div>
      {error && <span className={styles.error}>{error}</span>}
    </div>
  );
}
